import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { chunk, cloneDeep, first, uniq } from 'lodash-es';
import { EMPTY, Observable, from, of } from 'rxjs';
import { catchError, concatMap, filter, map, mapTo, mergeMap, switchMap, take, withLatestFrom } from 'rxjs/operators';

import { ExtractActions } from './extract.actions';
import { ExtractSelectors } from './extract.selectors';
import { DEAL_COMPARE_DIFF_LIMIT } from '../../features/deal/constants/auto-diff-limit';
import { ViewerCommand } from '../../features/document-viewer/models/document-viewer.models';
import { CellValue, Column, ColumnId, ColumnItemValue } from '../../features/market-comparison/models/column.model';
import { DiffWorkerOutput } from '../../features/market-comparison/models/paragraph-diff.model';
import { DiffItemService } from '../../features/market-comparison/services/diff-item.service';
import { MarketComparisonService } from '../../features/market-comparison/services/market-comparison.service';
import { TopicService } from '../../features/market-comparison/services/topic.service';
import { FAIL_NOTIFICATION_CONFIGURATION } from '../../features/shared/constants/notification-configurations';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { DatapointFieldControlType } from '../../features/unified-datapoint/models/datapoint-field.models';
import { DatapointRuleService } from '../../features/unified-datapoint/services/datapoint-rule.service';
import { Datapoint } from '../../generated/graphql-models';
import { AppActions } from '../app/app.actions';
import { AppSelectors } from '../app/app.selectors';
import { ChecklistListSelectors } from '../checklist-list/checklist-list.selectors';
import { DealComparisonActions } from '../deal-comparison/deal-comparison.actions';
import { ExtractionRuleListActions } from '../extraction-rule-list/extraction-rule-list.actions';
import { ExtractionRuleListSelectors } from '../extraction-rule-list/extraction-rule-list.selectors';
import { RootState } from '../root-state.model';

@Injectable()
export class ExtractEffects {
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly marketComparisonService = inject(MarketComparisonService);
  private readonly topicService = inject(TopicService);
  private readonly diffItemService = inject(DiffItemService);
  private readonly datapointRuleService = inject(DatapointRuleService);

  private readonly COLUMNS_IN_CHUNK_SIZE = 4;

  startLoadListEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractActions.StartLoadList),
      switchMap((action) =>
        // Built-in checklist pairings resolve asynchronously after init. Until they load,
        // selectedChecklistTopicIds is withheld (undefined), so reading it now would resolve no
        // columns. When the action carries no explicit topicIds, wait for the pairings to load
        // before reading the selection so the columns load once instead of never.
        this.store.select(AppSelectors.builtInChecklistsLoaded).pipe(
          filter((builtInChecklistsLoaded) => !!action.topicIds || builtInChecklistsLoaded),
          take(1),
          withLatestFrom(this.store, this.store.select(ChecklistListSelectors.selectedChecklistTopicIds)),
          switchMap(([, store, selectedChecklistTopicIds]) => {
            const selectedTopics = action.topicIds || selectedChecklistTopicIds;
            const columns =
              store.topicList.topics?.reduce((acc, curr) => {
                if (selectedTopics?.includes(curr.id)) {
                  acc.push(...curr.columns);
                }
                return acc;
              }, []) || [];

            const chunks: Column[][] = chunk(columns, this.COLUMNS_IN_CHUNK_SIZE);

            return of(...chunks).pipe(
              map((c) =>
                ExtractActions.LoadList({
                  columns: c,
                  loadingColumns: c.reduce<Record<string, boolean>>((acc, col) => {
                    acc[col.id] = true;
                    return acc;
                  }, {}),
                  transactionIds: action.transactionIds,
                }),
              ),
            );
          }),
        ),
      ),
    ),
  );

  loadSingleByHashEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractActions.LoadTopicValueByHash),
      withLatestFrom(this.store, this.store.select(ChecklistListSelectors.selectedChecklistColumns)),
      mergeMap(([action, store, selectedColumns]) => {
        const datapointRule = store.extractionRuleList.rules?.find((r) => r.ruleHash === action.datapointHash);
        const columns = selectedColumns?.filter((c) => c.value === datapointRule?.id) || [];
        const list = { ...store.extract.list };

        return this.marketComparisonService.getColumnItemValueRecordByMatrix(columns, [action.transactionId], true, true).pipe(
          map((result) =>
            ExtractActions.LoadListSuccess({
              transactionIds: [action.transactionId],
              list: columns.reduce((acc, column) => {
                acc[column.id] = {
                  ...acc[column.id],
                  ...(first(result) || {}),
                };
                return acc;
              }, list),
              finishedColumns: {},
            }),
          ),
        );
      }),
    ),
  );

  loadValuesByHashesEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractActions.LoadTopicValuesByHashes),
      withLatestFrom(this.store, this.store.select(ChecklistListSelectors.selectedChecklistColumns)),
      mergeMap(([action, store, selectedColumns]) => {
        const hashes = new Set(action.changes.map((change) => change.datapointHash));
        const datapointRuleIds = new Set(
          store.extractionRuleList.rules?.filter((r) => hashes.has(r.ruleHash)).map((r) => r.id) || [],
        );
        const columns = selectedColumns?.filter((c) => datapointRuleIds.has(c.value as string)) || [];
        const transactionIds = uniq(action.changes.map((change) => change.transactionId));
        const list = { ...store.extract.list };

        if (!columns.length || !transactionIds.length) {
          return EMPTY;
        }

        return this.marketComparisonService.getColumnItemValueRecordByMatrix(columns, transactionIds, true, true).pipe(
          map((result) =>
            ExtractActions.LoadListSuccess({
              transactionIds,
              list: columns.reduce((acc, column, index) => {
                acc[column.id] = {
                  ...acc[column.id],
                  ...(result[index] || {}),
                };
                return acc;
              }, list),
              finishedColumns: {},
            }),
          ),
        );
      }),
    ),
  );

  loadListEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractActions.LoadList),
      mergeMap((action) =>
        this.marketComparisonService.getColumnItemValueRecordByMatrix(action.columns, action.transactionIds, true).pipe(
          map((list) =>
            ExtractActions.LoadListSuccess({
              transactionIds: action.transactionIds,
              list: action.columns.reduce<Record<ColumnId, ColumnItemValue>>((acc, column, index) => {
                acc[column.id] = list[index];
                return acc;
              }, {}),
              finishedColumns: action.columns.reduce<Record<string, boolean>>((acc, curr) => {
                acc[curr.id] = false;
                return acc;
              }, {}),
            }),
          ),
        ),
      ),
    ),
  );

  loadExtractOrder$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractActions.LoadExtractOrder),
      switchMap(() => {
        return this.topicService.getExtractOrder().pipe(
          map((order) => {
            return ExtractActions.LoadExtractOrderSuccess({ order: order || [] });
          }),
          reportEffectError(ExtractActions.LoadExtractOrderFail),
        );
      }),
    ),
  );

  updateExtractOrderEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractActions.UpdateExtractOrder),
      switchMap((action) =>
        this.topicService.updateExtractOrder(action.order).pipe(
          map((order) => ExtractActions.UpdateExtractOrderSuccess({ order })),
          reportEffectError(ExtractActions.UpdateExtractOrderFail),
        ),
      ),
    ),
  );

  makeDiff$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractActions.MakeDiff),
      withLatestFrom(this.store),
      switchMap(([action, store]) => {
        return action.mode === 'clear'
          ? of(ExtractActions.ClearDiffByTopic({ topicId: action.topic.id }))
          : of(...action.topic.columns).pipe(
              map((column) =>
                ExtractActions.MakeDiffChunk({
                  column,
                  baseline: action.baseline,
                  orderedTransactionIds: action.orderedTransactionIds,
                  topicId: action.topic.id,
                }),
              ),
            );
      }),
    ),
  );

  makeDiffChunk$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractActions.MakeDiffChunk),
      withLatestFrom(this.store, this.store.select(ExtractSelectors.extractForDealCompare)),
      concatMap(([action, store, data]) => {
        try {
          const cells = Object.values(data[action.column.id] || {});

          const diff$: Observable<DiffWorkerOutput> = cells.length
            ? this.diffItemService.diffWithWorker(action.baseline, cells as CellValue[], action.orderedTransactionIds)
            : of(undefined);

          return diff$.pipe(
            withLatestFrom(this.store),
            map(([result, currentStore]) => {
              const diff = cloneDeep(currentStore.extract.diffsByTopic[action.topicId]);

              if (!diff) {
                return ExtractActions.MakeDiffFail({ error: 'No diff found' });
              }

              diff.diffBaseline[action.column.id] = result?.baseline;
              diff.diffColumns[action.column.id] = !!result;
              diff.diffMaximums[action.column.id] = result?.diffMaximums;
              diff.diffList[action.column.id] = result?.diffValues;
              diff.diffLoadingByColumns[action.column.id] = false;

              const completed = Object.keys(diff.diffLoadingByColumns)
                .filter((key) => !!diff.diffColumns[key])
                .every((key) => diff.diffLoadingByColumns[key] === false);

              diff.diffLoading = !completed;
              diff.diffCompleted = completed;

              return ExtractActions.MakeDiffSuccess({
                topicId: action.topicId,
                diff,
              });
            }),
            catchError((error) => {
              console.error(error);
              return of(ExtractActions.MakeDiffFail({ error }));
            }),
          );
        } catch (error) {
          console.error(error);
          return of(ExtractActions.MakeDiffFail({ error }));
        }
      }),
    ),
  );

  clearDiffSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DealComparisonActions.AddToBasket,
        DealComparisonActions.SetBasket,
        DealComparisonActions.RemoveFromBasket,
        // SetCompare fires whenever the compared deal set changes (basket <-> deal filter switch,
        // filter or limit change). A diff computed for the previous set is keyed by its transaction
        // ids, so keeping it would leave paragraph cells in diff mode with no regions for the new
        // deals; clearing lets diffByDefault$ re-diff the new set once its values load.
        DealComparisonActions.SetCompare,
      ),
      map(() => ExtractActions.ClearDiff()),
    ),
  );

  diffAll$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractActions.DiffAll),
      withLatestFrom(this.store),
      switchMap(([action, store]) => {
        return action.mode === 'diff'
          ? of(...action.topics).pipe(
              map((topic) =>
                ExtractActions.MakeDiff({
                  topic,
                  baseline: action.baseline,
                  orderedTransactionIds: action.orderedTransactionIds,
                  mode: 'diff',
                }),
              ),
            )
          : of(ExtractActions.ClearDiff());
      }),
    ),
  );

  diffByDefault$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractActions.LoadListSuccess),
      withLatestFrom(this.store, this.store.select(ChecklistListSelectors.selectedChecklistTopicIds)),
      filter(([action, store, selectedChecklistTopicIds]) => {
        const dealCount = action.transactionIds?.length || 0;

        return dealCount >= 2 && dealCount <= DEAL_COMPARE_DIFF_LIMIT;
      }),
      switchMap(([action, store, selectedChecklistTopicIds]) => {
        const topics = store.topicList.topics?.filter((t) => selectedChecklistTopicIds?.includes(t.id)) || [];

        const loadedTopics = topics.filter(
          (topic) => topic.columns.every((c) => store.extract.loadingColumns[c.id] === false) && !store.extract.diffsByTopic[topic.id],
        );

        const transactionIds = store.dealComparison.deals.map((d) => d.transactionId);

        return of(...loadedTopics).pipe(
          map((topic) =>
            ExtractActions.MakeDiff({
              topic,
              orderedTransactionIds: transactionIds,
              mode: 'diff',
              baseline: first(transactionIds),
            }),
          ),
        );
      }),
    ),
  );

  updateDatapointEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractActions.UpdateDatapoint),
      withLatestFrom(this.store),
      concatMap(([action, store]) => {
        return this.marketComparisonService
          .upsertDatapoint(
            action.event.columnId,
            action.event.transactionId,
            action.event.datapointHash,
            action.event.value,
            action.event.sourceReferences,
          )
          .pipe(
            map((datapoint) =>
              ExtractActions.UpdateDatapointSuccess({
                datapoint,
                event: action.event,
              }),
            ),
            reportEffectError(ExtractActions.UpdateDatapointFail),
          );
      }),
    ),
  );

  updateDatapointFail$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractActions.UpdateDatapointFail),
      map(() =>
        AppActions.PushNotification({
          message: 'Failed to update the datapoint',
          config: FAIL_NOTIFICATION_CONFIGURATION,
        }),
      ),
    ),
  );

  viewerModifyDatapoint$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractActions.ViewerModifyDatapoint),
      filter((action) => {
        const [viewerType, viewerValue] = action.viewerPayload;
        return (
          viewerType === ViewerCommand.SelectedSourceReference ||
          [DatapointFieldControlType.Textbox, DatapointFieldControlType.MultilineTextbox, DatapointFieldControlType.Number].includes(
            action.target.datapointFieldControlType,
          )
        );
      }),
      mergeMap((action) => {
        return this.store.select(ExtractSelectors.updatingDatapoint).pipe(
          filter((val) => !val),
          take(1),
          mapTo(action),
        );
      }),
      withLatestFrom(this.store),
      map(([action, store]) => {
        const datapoint = (
          store.extract.list[action.target.columnId] ? store.extract.list[action.target.columnId][action.target.transactionId] : undefined
        ) as Partial<Datapoint>;

        return {
          action,
          transactionId: action.target.transactionId,
          datapointHash: action.target.datapointHash,
          columnId: action.target.columnId,
          datapoint,
        };
      }),
      map(({ action, transactionId, columnId, datapointHash, datapoint }) => {
        const [viewerType, viewerValue] = action.viewerPayload;
        let value = datapoint?.value;
        let sourceReferences = datapoint?.sourceReferences || [];

        switch (viewerType) {
          case ViewerCommand.AppendData:
            value = value ? `${value} ${viewerValue}` : viewerValue;
            break;
          case ViewerCommand.ReplaceData:
            value = viewerValue;
            break;
          case ViewerCommand.SelectedSourceReference:
            sourceReferences = [...sourceReferences, viewerValue];
            break;

          default:
            break;
        }

        const event = {
          transactionId,
          columnId,
          datapointHash,
          value,
          sourceReferences,
        };

        return ExtractActions.UpdateDatapoint({
          event,
        });
      }),
    ),
  );

  rerunDatapoint$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractActions.ReRunSingleDatapoint),
      withLatestFrom(this.store.select(ExtractionRuleListSelectors.rulesInRecord)),
      concatMap(([action, ruleLookup]) => {
        return this.datapointRuleService.runRule(ruleLookup[action.datapointRuleId], [action.transactionId], 'priority').pipe(
          map(() => ExtractActions.ReRunSingleDatapointInitiated(action)),
          reportEffectError(ExtractActions.ReRunSingleDatapointFailed),
        );
      }),
    ),
  );

  rerunDatapointsBatch$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractActions.ReRunDatapointsBatch),
      withLatestFrom(this.store.select(ExtractionRuleListSelectors.rulesInRecord)),
      concatMap(([action, ruleLookup]) =>
        from(chunk(action.datapoints, 5)).pipe(
          concatMap((datapointChunk) =>
            from(datapointChunk).pipe(
              mergeMap((datapoint) => {
                const rule = ruleLookup[datapoint.datapointRuleId];

                if (!rule) {
                  return of(
                    ExtractActions.ReRunSingleDatapointFailed({
                      error: `Datapoint rule ${datapoint.datapointRuleId} is not loaded`,
                    }),
                  );
                }

                return this.datapointRuleService.runRule(rule, [datapoint.transactionId], 'priority').pipe(
                  map(() => ExtractActions.ReRunSingleDatapointInitiated(datapoint)),
                  reportEffectError(ExtractActions.ReRunSingleDatapointFailed),
                );
              }),
            ),
          ),
        ),
      ),
    ),
  );

  rerunDatapointInitiated$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtractActions.ReRunSingleDatapointInitiated),
      map((action) => ExtractionRuleListActions.LoadSingleRule({ id: action.datapointRuleId })),
    ),
  );
}
